<template>
  <div ref="containerRef" :id="id" :class="rootClass">
    <div
      v-for="(section, index) in suggestions"
      :key="`${section.title}-${index}`"
      class="search-bar-suggestions__section"
    >
        <TypographyDiscoverMode tag="h2"
                                tag-style="headlineSmall"
                                weight="bold"
                                class="search-bar-suggestions__heading">
            {{ section.title }}
        </TypographyDiscoverMode>
      <ul class="search-bar-suggestions__list">
        <li
          v-for="(item, itemIndex) in section.items"
          :key="`${item.title}-${itemIndex}`"
        >
          <a
            :href="item.url"
            class="search-bar-suggestions__link"
            :class="{
              'search-bar-suggestions__link--hasImage': Boolean(item.image),
            }"
            @click="handleItemClick(item, itemIndex)"
            @keydown="handleKeyDown"
          >
            <img
              v-if="item.image"
              class="search-bar-suggestions__image"
              alt=""
              width="48"
              height="48"
              :src="imageUrl(item.image, 48)"
              :srcset="`${imageUrl(item.image, 48)} 1x, ${imageUrl(
                item.image,
                96,
              )} 2x`"
            />
            <div class="search-bar-suggestions__titleWrap">
              <div
                class="search-bar-suggestions__title"
                v-html="item.titleHighlighted || item.title"
              />
              <div
                v-if="item.subtitle || item.subtitleHighlighted"
                class="search-bar-suggestions__subtitle"
                v-html="item.subtitleHighlighted || item.subtitle || ''" 
              />
              <BrandLogo v-if="item.brand" :brand="item.brand" />
            </div>
            <Icon
              v-if="item.icon"
              class="search-bar-suggestions__itemIcon"
              :name="item.icon"
              :size="16"
              hidden
            />
          </a>
        </li>
      </ul>
      <a
        v-if="section.bottomLink"
        :href="section.bottomLink.url"
        class="search-bar-suggestions__bottomLink"
        @click="handleBottomLinkClick($event, section)"
        @keydown="handleKeyDown"
      >
        {{ section.bottomLink.title }}
        <Icon
          name="ArrowRight_16"
          class="search-bar-suggestions__bottomIcon"
          hidden
        />
      </a>
    </div>
  </div>
</template>

<script>
    import BrandLogo from "@/components/BrandLogo.vue";
    import Icon from "@/components/Icon.vue";
    import TypographyDiscoverMode from "@/components/TypographyDiscoverMode.vue"

    export default {
        name: "SearchBarSuggestions",
        components: { BrandLogo, Icon, TypographyDiscoverMode },

        props: {
            id: {
                type: String,
                default: "",
            },
            query: {
                type: String,
                default: "",
            },
            suggestions: {
                type: Array,
                required: true,
            },
            /** When true, primary-click on bottom link emits instead of navigating (parent handles). */
            interceptBottomLink: {
                type: Boolean,
                default: false,
            },
    },

    emits: ["bottom-link-click", "escape", "focus-out", "item-click", "show-all-click"],

    computed: {
        rootClass() {
            return [
                "search-bar-suggestions__panel",
                { multipleSections: this.suggestions.length > 1 },
            ];
        },
    },

    methods: {
        imageUrl(src, size) {
          if (!src) return "";

          try {
            const u = new URL(src, window.location.origin);

            u.searchParams.set("w", String(size));
            u.searchParams.set("h", String(size));

            return u.toString();
          } catch {
            const joiner = src.includes("?") ? "&" : "?";

            return `${src}${joiner}w=${size}&h=${size}`;
          }
        },

    focusFirstAnchor() {
        this.$refs.containerRef?.querySelector("a")?.focus();
    },

    handleItemClick(item, itemIndex) {
        this.$emit("item-click", {
            item,
            position: itemIndex,
        });
    },

    handleBottomLinkClick(event, section) {
        if (
            event.button !== 0 ||
            event.ctrlKey ||
            event.metaKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }

        const keyword = (this.query || "").trim();

        if (!keyword) return;

        this.$emit("show-all-click", {
            destinationUrl: section?.bottomLink?.url || "",
            searchTerm: keyword,
        });

        if (!this.interceptBottomLink) return;

        event.preventDefault();
        this.$emit("bottom-link-click", keyword);
    },

    handleKeyDown(event) {
      const root = this.$refs.containerRef;

            if (!root) return;

            const focusable = Array.from(root.querySelectorAll("a"));
            const currentIndex = focusable.indexOf(document.activeElement);

            switch (event.key) {
                case "Escape": {
                    event.preventDefault();
                    this.$emit("escape");
                    break;
                }

                case "ArrowDown": {
                    event.preventDefault();
                    const next = (currentIndex + 1) % focusable.length;

                    focusable[next]?.focus();
                    break;
                }

                case "ArrowUp": {
                    event.preventDefault();

                    if (currentIndex === 0) {
                    this.$emit("focus-out");
                    break;
                    }

                    const prev = (currentIndex - 1 + focusable.length) % focusable.length;

                    focusable[prev]?.focus();
                    break;
                }

                case "Home": {
                    event.preventDefault();
                    focusable[0]?.focus();
                    break;
                }

                case "End": {
                    event.preventDefault();
                    focusable[focusable.length - 1]?.focus();
                    break;
                }

                default:
            }
        },
    },
};
</script>
